import { useMemo } from "react";
import { useDecisionMetrics, VehicleDecisionData } from "./useDecisionMetrics";

export type AlertSeverity = "high" | "medium" | "low";
export type AlertStatus = "new" | "acknowledged" | "resolved";

export type AlertType =
  | "expense_spike"
  | "profit_drop"
  | "idle_vehicle"
  | "cleaning_outlier"
  | "maintenance_spike"
  | "high_expense_ratio"
  | "revenue_decline";

const alertTypeLabels: Record<AlertType, string> = {
  expense_spike: "Expense Spike",
  profit_drop: "Profit Drop",
  idle_vehicle: "Idle Vehicle",
  cleaning_outlier: "Cost Outlier",
  maintenance_spike: "Maintenance Spike",
  high_expense_ratio: "High Expense Ratio",
  revenue_decline: "Revenue Decline",
};

export interface FleetAlert {
  id: string;
  type: AlertType;
  typeLabel: string;
  vehicleId: string;
  vehicleName: string;
  description: string;
  whyTriggered: string;
  severity: AlertSeverity;
  timestamp: Date;
  status: AlertStatus;
  details: {
    baseline: { label: string; value: string }[];
    comparison: { label: string; value: string }[];
  };
}

function vehicleName(v: VehicleDecisionData): string {
  return v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
}

export const useFleetAlerts = () => {
  const { vehicleData, fleetAvg, isLoading } = useDecisionMetrics();

  const alerts = useMemo<FleetAlert[]>(() => {
    if (!vehicleData.length) return [];
    const result: FleetAlert[] = [];
    const now = new Date();

    for (const v of vehicleData) {
      if (v.tripCount === 0) continue;
      const name = vehicleName(v);

      // 1. Expense spike — last month expenses 40%+ above prior month
      if (v.priorMonthExpenses > 30 && v.recentExpenseChange > 40) {
        result.push({
          id: `alert-expense-spike-${v.vehicle.id}`,
          type: "expense_spike",
          typeLabel: alertTypeLabels.expense_spike,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          description: `Expenses jumped ${Math.round(v.recentExpenseChange)}% last month — $${Math.round(v.lastMonthExpenses)} vs $${Math.round(v.priorMonthExpenses)} prior.`,
          whyTriggered: `Last month's expenses exceeded the prior month by more than 40%, which is outside the normal range for this vehicle.`,
          severity: v.recentExpenseChange > 70 ? "high" : "medium",
          timestamp: new Date(now.getFullYear(), now.getMonth() - 1, 28),
          status: "new",
          details: {
            baseline: [
              { label: "Prior month expenses", value: `$${Math.round(v.priorMonthExpenses)}` },
              { label: "Vehicle avg monthly expense (ongoing)", value: `$${Math.round(v.ongoingExpenses / Math.max(v.monthsActive, 1))}` },
            ],
            comparison: [
              { label: "Last month expenses", value: `$${Math.round(v.lastMonthExpenses)}` },
              { label: "Change", value: `+${Math.round(v.recentExpenseChange)}%` },
            ],
          },
        });
      }

      // 2. Profit dropped 30%+ month-over-month (approximated)
      if (
        v.monthsActive >= 2 &&
        v.monthlyProfit < fleetAvg.monthlyProfit * 0.7 &&
        v.monthlyProfit < 100 &&
        v.totalEarnings > 0
      ) {
        const dropPct = fleetAvg.monthlyProfit > 0
          ? Math.round(((fleetAvg.monthlyProfit - v.monthlyProfit) / fleetAvg.monthlyProfit) * 100)
          : 0;
        if (dropPct > 30) {
          result.push({
            id: `alert-profit-drop-${v.vehicle.id}`,
            type: "profit_drop",
            typeLabel: alertTypeLabels.profit_drop,
            vehicleId: v.vehicle.id,
            vehicleName: name,
            description: `Monthly profit is ${dropPct}% below the fleet average at $${Math.round(v.monthlyProfit)}/mo.`,
            whyTriggered: `This vehicle's monthly profit deviates significantly from the fleet average of $${Math.round(fleetAvg.monthlyProfit)}/mo.`,
            severity: dropPct > 60 ? "high" : "medium",
            timestamp: new Date(now.getFullYear(), now.getMonth(), 1),
            status: "new",
            details: {
              baseline: [
                { label: "Fleet avg monthly profit", value: `$${Math.round(fleetAvg.monthlyProfit)}` },
              ],
              comparison: [
                { label: "This vehicle monthly profit", value: `$${Math.round(v.monthlyProfit)}` },
                { label: "Deviation", value: `-${dropPct}%` },
              ],
            },
          });
        }
      }

      // 3. No revenue for 14+ days (low utilization proxy)
      if (v.utilization < 10 && v.monthsActive >= 1 && v.tripDays < 3) {
        result.push({
          id: `alert-idle-${v.vehicle.id}`,
          type: "idle_vehicle",
          typeLabel: alertTypeLabels.idle_vehicle,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          description: `Only ${v.tripDays} trip days recorded over ${v.monthsActive} month(s) — vehicle appears idle.`,
          whyTriggered: `Utilization is ${Math.round(v.utilization)}%, significantly below the fleet average of ${Math.round(fleetAvg.utilization)}%.`,
          severity: v.utilization < 5 ? "high" : "medium",
          timestamp: new Date(now.getFullYear(), now.getMonth(), 5),
          status: "new",
          details: {
            baseline: [
              { label: "Fleet avg utilization", value: `${Math.round(fleetAvg.utilization)}%` },
              { label: "Expected trip days/mo", value: `${Math.round(fleetAvg.utilization * 0.3)}+` },
            ],
            comparison: [
              { label: "This vehicle utilization", value: `${Math.round(v.utilization)}%` },
              { label: "Total trip days", value: `${v.tripDays}` },
            ],
          },
        });
      }

      // 4. Sudden maintenance spike
      if (v.maintenanceCount >= 3 && v.monthsActive <= 3) {
        result.push({
          id: `alert-maint-spike-${v.vehicle.id}`,
          type: "maintenance_spike",
          typeLabel: alertTypeLabels.maintenance_spike,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          description: `${v.maintenanceCount} maintenance tasks in ${v.monthsActive} month(s) — higher than expected.`,
          whyTriggered: `The maintenance frequency for this vehicle is unusually high for its active period, suggesting a possible reliability issue.`,
          severity: v.maintenanceCount >= 5 ? "high" : "medium",
          timestamp: new Date(now.getFullYear(), now.getMonth(), 10),
          status: "new",
          details: {
            baseline: [
              { label: "Expected tasks/3mo", value: "1–2" },
            ],
            comparison: [
              { label: "Actual tasks", value: `${v.maintenanceCount}` },
              { label: "Time period", value: `${v.monthsActive} month(s)` },
            ],
          },
        });
      }

      // 5. High expense ratio outlier (relative to fleet)
      if (
        v.expenseRatio > fleetAvg.expenseRatio * 1.5 &&
        v.expenseRatio > 70 &&
        v.totalEarnings > 100
      ) {
        result.push({
          id: `alert-expense-ratio-${v.vehicle.id}`,
          type: "high_expense_ratio",
          typeLabel: alertTypeLabels.high_expense_ratio,
          vehicleId: v.vehicle.id,
          vehicleName: name,
          description: `Expense ratio of ${Math.round(v.expenseRatio)}% is significantly above the fleet average of ${Math.round(fleetAvg.expenseRatio)}%.`,
          whyTriggered: `This vehicle's expenses consume a disproportionate share of its earnings compared to the rest of the fleet.`,
          severity: v.expenseRatio > 90 ? "high" : "medium",
          timestamp: new Date(now.getFullYear(), now.getMonth(), 3),
          status: "new",
          details: {
            baseline: [
              { label: "Fleet avg expense ratio", value: `${Math.round(fleetAvg.expenseRatio)}%` },
            ],
            comparison: [
              { label: "This vehicle expense ratio (ongoing)", value: `${Math.round(v.expenseRatio)}%` },
              { label: "Ongoing expenses", value: `$${Math.round(v.ongoingExpenses)}` },
              { label: "Total earnings", value: `$${Math.round(v.totalEarnings)}` },
            ],
          },
        });
      }
    }

    // Sort reverse chronological
    return result.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [vehicleData, fleetAvg]);

  return { alerts, vehicleData, isLoading };
};
